import {PaymentProvider} from '@gatewee/types/provider';
import {Domain} from './domain';
import {PaymentMethod} from './payment';
import {Recipient} from './recipient';

export interface FeeSnapshotItem {
  type: FeeType;
  /** Legacy snapshots; omitted when fees are tier-based. */
  value?: number;
  /** Tier bands with fixed + rate per range (authoritative for v2 snapshots). */
  tiers?: FeeTier[];
  /** Optional fee-v2 aliases seen in some payloads. */
  fixedValue?: number | string | null;
  fixedRate?: number | string | null;
  fixedAmount?: number | string | null;
  rateValue?: number | string | null;
  /** Omitted for cash-out fees on some API payloads */
  paymentMethod?: PaymentMethod;
  operation?: FlowDirection;
  installments?: number;
  /** Credit card acquirer brand; omit or null = brandless / any brand */
  brand?: string | null;
  retention?: number;
}

export interface FeeSnapshot {
  domain: FeeSnapshotItem[];
  recipient?: FeeSnapshotItem[];
}

/** Pre-merged effective fees (e.g. recipient GET /snapshot as a bare array). */
export function toEffectiveFeeSnapshot(items: FeeSnapshotItem[]): FeeSnapshot {
  return {domain: items, recipient: []};
}

/** Accept legacy FeeSnapshot or bare FeeSnapshotItem[] from recipient snapshot. */
export function normalizeFeeSnapshotResponse(
  raw: FeeSnapshot | FeeSnapshotItem[],
): FeeSnapshot {
  if (Array.isArray(raw)) {
    return toEffectiveFeeSnapshot(raw);
  }
  return {
    domain: raw.domain ?? [],
    recipient: raw.recipient ?? [],
  };
}

export enum FlowDirection {
  INCOME = 'income',
  CASH_OUT = 'cash_out',
}

export interface RecipientFee {
  createdBy: string;
  updatedBy: string;
  id: number;
  value: string;
  type: 'rate' | 'amount';
  paymentMethod: PaymentMethod;
  operation: FlowDirection;
  createdAt: Date;
  updatedAt: Date;
  domain: Domain;
  recipient: Recipient;
}

export type FeeType = 'amount' | 'rate' | 'compound';

export type ProvidersBaseFees = {
  [value in PaymentProvider]: FeeSnapshotItem[];
};

// --- Fee Plan V2 ---

export interface FeePlan {
  id: number;
  name: string;
  description?: string;
  scope: 'gatewee' | 'domain';
  isDefault: boolean;
  status: 'active' | 'archived';
  rules: FeeRule[];
}

export interface FeeRule {
  id?: number;
  operation: FlowDirection;
  paymentMethod: PaymentMethod | null;
  installments: number | null;
  brand: string | null;
  tiers: FeeTier[];
}

export interface FeeTier {
  id?: number;
  minAmount: number;
  maxAmount: number | null;
  fixedAmount: number;
  rateValue: number;
  sortOrder: number;
}

export interface EffectiveFeePlanResponse {
  plan: FeePlan | null;
  source: 'assignment' | 'default' | 'none';
}

/** Recipients explicitly assigned to a domain-scoped fee plan (GET .../plans/:planId/assignments). */
export interface FeePlanRecipientAssignment {
  recipientId: number;
  recipientName: string;
  planId?: number;
}
